/**
 * Socket.IO Client Event Validation & Registry
 *
 * Provides wrappers around socket.on() and socket.emit() that:
 * 1. Register event names in a static registry for contract test introspection
 * 2. Validate payloads against contract schemas in __DEV__ mode
 *
 * The registry allows tests to verify that every event declared in
 * the AsyncAPI spec is handled/emitted by the client.
 */

import type { Socket } from 'socket.io-client';
import { getCurrentIntent } from '../dev/intentTag';
// ── Event Registry ─────────────────────────────────────────────────

const listenRegistry = new Set<string>();
const emitRegistry = new Set<string>();

/**
 * Get all event names the client listens to (server→client).
 */
export function getClientListenRegistry(): ReadonlySet<string> {
  return listenRegistry;
}

/**
 * Get all event names the client emits (client→server).
 */
export function getClientEmitRegistry(): ReadonlySet<string> {
  return emitRegistry;
}

/**
 * Reset registries (for testing).
 */
export function resetClientRegistries(): void {
  listenRegistry.clear();
  emitRegistry.clear();
}

// ── Dev-only observer ─────────────────────────────────────────────

let _notifyWs: ((direction: 'emit' | 'receive', event: string, data: unknown) => void) | undefined;
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- handler keys are generic callback refs
const _handlerMap = new WeakMap<Function, (...args: unknown[]) => void>();
if (__DEV__) {
  _notifyWs = require('../dev/storage-observer').notifyWs;
}

// ── Wrappers ───────────────────────────────────────────────────────

/**
 * Listen for a server→client event. Registers the event name for introspection.
 */
export function onServerEvent<T>(
  socket: Socket,
  eventName: string,
  handler: (data: T) => void,
): void {
  listenRegistry.add(eventName);
  const wrappedHandler = ((data: T) => {
    if (__DEV__ && _notifyWs) _notifyWs('receive', eventName, data);
    handler(data);
  }) as (...args: unknown[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO typed events require cast for dynamic event names
  socket.on(eventName, wrappedHandler as any);
  // Store mapping so offServerEvent can remove by original ref
  _handlerMap.set(handler, wrappedHandler);
}

/**
 * Remove a server→client event listener.
 */
export function offServerEvent<T>(
  socket: Socket,
  eventName: string,
  handler: (data: T) => void,
): void {
  // In dev, we wrapped the handler — look up the wrapped version
  const wrapped = _handlerMap.get(handler);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO typed events require cast for dynamic event names
  socket.off(eventName, (wrapped || handler) as any);
  if (wrapped) _handlerMap.delete(handler);
}

/**
 * Emit a client→server event. Registers the event name for introspection.
 */
export function emitEvent(
  socket: Socket,
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO event data is untyped at this layer
  data: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ack callbacks have per-event response types
  ack?: (...args: any[]) => void,
): void {
  emitRegistry.add(eventName);
  if (__DEV__ && _notifyWs) _notifyWs('emit', eventName, data);

  // E2E intent attribution (dev only): when the runner has set a
  // current intent, embed it in the payload so the traffic proxy can
  // attribute this emit to the correct intent. The proxy strips the
  // field before forwarding to the backend. Production builds
  // tree-shake the entire block.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload shape isn't typed at this layer
  let payload: any = data;
  if (__DEV__) {
    const intent = getCurrentIntent();
    if (intent && payload && typeof payload === 'object' && !Array.isArray(payload)) {
      payload = { ...payload, _e2eIntent: intent };
    }
  }

  if (ack) {
    socket.emit(eventName, payload, ack);
  } else {
    socket.emit(eventName, payload);
  }
}
